<template>
  <section
    class="guided-mode-sequential-group"
    :class="{ 'guided-mode-sequential-group--transitioning': transitionActive }"
  >
    <guided-mode-visual
      v-if="groupMedia"
      :media="groupMedia"
      :fallback-icon="mdiImageOutline"
      :size="groupMediaState.size"
      interactive
      @toggle-size="withAnchor(0, () => $emit('toggle-group-size'))"
    />
    <p v-if="instructions" class="guided-mode-sequential-group__instructions">{{ instructions }}</p>

    <div class="guided-mode-sequential-group__questions">
      <div
        v-for="(item, index) in visibleQuestionItems"
        ref="question"
        :key="item.conceptId || item.prompt"
        :data-concept-id="item.conceptId"
        class="guided-mode-sequential-group__question"
      >
        <GuidedModeBloodPressure
          v-if="item.collectionType === 'blood_pressure'"
          :prompt="item.prompt"
          :instructions="''"
          :media="item.media"
          :media-size="mediaSizeFor(item.conceptId)"
          media-interactive
          :systolic="item.bloodPressureSystolic"
          :diastolic="item.bloodPressureDiastolic"
          :systolic-invalid="item.bloodPressureSystolicInvalid"
          :diastolic-invalid="item.bloodPressureDiastolicInvalid"
          :systolic-warning="item.bloodPressureSystolicWarning"
          :diastolic-warning="item.bloodPressureDiastolicWarning"
          :ranges="item.bloodPressureRanges"
          :display-unit="'mmHg'"
          :validation-severity="item.validationSeverity"
          :validation-message-token="item.validationMessageToken"
          :allow-unknown="item.allowUnknown"
          :unknown="item.bloodPressureUnknown"
          @part-changed="(part, value) => withAnswerTransition(index, () => $emit('blood-pressure-part', {
            conceptId: item.conceptId, part, value,
          }))"
          @unknown="withAnswerTransition(index, () => $emit('blood-pressure-unknown', { conceptId: item.conceptId }))"
          @toggle-size="withAnchor(index, () => $emit('toggle-size', { conceptId: item.conceptId }))"
          @entry-complete="withAnswerTransition(index, () => $emit('entry-complete', { conceptId: item.conceptId }))"
        />
        <GuidedModeDate
          v-else-if="item.collectionType === 'calculated_date'"
          :prompt="item.prompt"
          :media="item.media"
          :media-size="mediaSizeFor(item.conceptId)"
          media-interactive
          :value="item.dateValue"
          calculated
          @toggle-size="withAnchor(index, () => $emit('toggle-size', { conceptId: item.conceptId }))"
        />
        <GuidedModeDate
          v-else-if="item.collectionType === 'date'"
          :prompt="item.prompt"
          :media="item.media"
          :media-size="mediaSizeFor(item.conceptId)"
          media-interactive
          :value="item.dateValue"
          :min="item.dateMin"
          :max="item.dateMax"
          :invalid="item.dateInvalid"
          @date-changed="value => withAnswerTransition(index, () => $emit('date-changed', {
            conceptId: item.conceptId, value,
          }))"
          @toggle-size="withAnchor(index, () => $emit('toggle-size', { conceptId: item.conceptId }))"
          @entry-complete="withAnswerTransition(index, () => $emit('entry-complete', { conceptId: item.conceptId }))"
        />
        <GuidedModeQuestion
          v-else
          :prompt="item.prompt"
          :instructions="''"
          :media="item.media"
          :media-size="mediaSizeFor(item.conceptId)"
          media-interactive
          :audio-enabled="audioEnabledFor(item.conceptId)"
          :playback-rate="item.playbackRate"
          :options="item.options"
          :collection="item.collection"
          :answer="item.answer"
          :selected-values="item.selectedValues"
          :multiple="item.multiple"
          :display-unit="item.displayUnit"
          :numeric-step="item.numericStep"
          :widget="item.widget"
          :input-id="item.inputId"
          :validation-severity="item.validationSeverity"
          :validation-message-token="item.validationMessageToken"
          :allow-unknown="item.allowUnknown"
          @answer="value => withAnswerTransition(index, () => $emit('answer', {
            conceptId: item.conceptId, value,
          }))"
          @option-selected="(value) => withAnswerTransition(index, () => $emit('option-selected', { conceptId: item.conceptId, value }))"
          @toggle-size="withAnchor(index, () => $emit('toggle-size', { conceptId: item.conceptId }))"
          @toggle-audio="$emit('toggle-audio', { conceptId: item.conceptId })"
          @entry-complete="withAnswerTransition(index, () => $emit('entry-complete', { conceptId: item.conceptId }))"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mdiImageOutline } from '@mdi/js'
import GuidedModeQuestion from './GuidedModeQuestion.vue'
import GuidedModeBloodPressure from './GuidedModeBloodPressure.vue'
import GuidedModeDate from './GuidedModeDate.vue'
import GuidedModeVisual from './GuidedModeVisual.vue'
import type { GuidedModeMedia, SequentialGroupQuestionItem } from '../types/guided-mode.types'
import type { GuidedMediaPresentation } from '../composables/useGuidedMediaPresentation'

interface VisualSnapshot {
  element: HTMLElement
  rect: DOMRect
  marginBottom: string
  group: boolean
}

export default defineComponent({
  name: 'GuidedModeSequentialGroup',
  components: { GuidedModeQuestion, GuidedModeBloodPressure, GuidedModeDate, GuidedModeVisual },
  props: {
    groupTitle: { type: String, required: true },
    instructions: { type: String, default: '' },
    questionItems: { type: Array as PropType<SequentialGroupQuestionItem[]>, default: () => [] },
    mediaState: {
      type: Object as PropType<Record<string, GuidedMediaPresentation>>,
      default: () => ({}),
    },
    groupMedia: { type: Object as PropType<GuidedModeMedia | null>, default: null },
    groupMediaState: {
      type: Object as PropType<GuidedMediaPresentation>,
      default: () => ({ size: 'default' }),
    },
  },
  emits: [
    'answer',
    'option-selected',
    'blood-pressure-part',
    'blood-pressure-unknown',
    'date-changed',
    'toggle-size',
    'toggle-group-size',
    'toggle-audio',
    'entry-complete',
  ],
  data: () => ({
    mdiImageOutline,
    transitionId: 0,
    transitionActive: false,
    positionFrame: null as number | null,
    resizeAnimations: [] as Animation[],
  }),
  computed: {
    visibleQuestionItems(): SequentialGroupQuestionItem[] {
      return this.questionItems.filter(item => (
        item.collectionType !== 'calculated_date' || Boolean(item.dateValue)
      ))
    },
  },
  beforeDestroy() {
    this.cancelTransitions()
  },
  methods: {
    cancelTransitions(): void {
      this.transitionId += 1
      this.transitionActive = false
      if (this.positionFrame !== null) window.cancelAnimationFrame(this.positionFrame)
      this.positionFrame = null
      for (const animation of this.resizeAnimations) animation.cancel()
      this.resizeAnimations = []
    },
    questionElement(conceptId?: string): HTMLElement | undefined {
      if (!conceptId) return undefined
      const questions = this.$refs.question as HTMLElement[] | undefined
      return questions?.find(element => element.dataset.conceptId === conceptId)
    },
    withAnswerTransition(index: number, emit: () => void): void {
      const item = this.visibleQuestionItems[index]
      const conceptId = item?.conceptId
      const question = this.questionElement(conceptId)
      if (!conceptId || !question) {
        this.cancelTransitions()
        emit()
        return
      }
      const focused = document.activeElement instanceof HTMLElement
        && question.contains(document.activeElement) ? document.activeElement : null
      const anchor = focused
        || question.querySelector<HTMLElement>('.assessment-option-input') || question
      const anchorTop = anchor.getBoundingClientRect().top
      const interruptedMultiple = item.multiple && this.positionFrame !== null
      this.cancelTransitions()
      const transitionId = this.transitionId
      const previousSize = this.mediaSizeFor(conceptId)
      const previousGroupSize = this.groupMediaState.size
      const preservePosition = () => {
        if (!this.$el.contains(anchor)) return
        // Native anchoring may already have corrected part or all of the movement.
        const delta = anchor.getBoundingClientRect().top - anchorTop
        if (delta) window.scrollBy(0, delta)
      }
      const snapshots: VisualSnapshot[] = []
      const capture = (visual: HTMLElement | null | undefined, group: boolean) => {
        if (!visual) return
        const elements = [visual, visual.querySelector<HTMLElement>('img, video, :scope > .v-icon')]
        for (const element of elements) {
          if (!element) continue
          snapshots.push({
            element,
            rect: element.getBoundingClientRect(),
            marginBottom: window.getComputedStyle(element).marginBottom,
            group,
          })
        }
      }
      if (this.mediaSizeFor(conceptId) === 'default') {
        capture(question?.querySelector<HTMLElement>('.guided-mode__visual'), false)
      }
      if (this.groupMediaState.size === 'default') {
        capture(this.$el.querySelector<HTMLElement>(':scope > .guided-mode__visual'), true)
      }
      this.transitionActive = snapshots.length > 0
      emit()
      void this.$nextTick(async () => {
        if (transitionId !== this.transitionId) return
        const isCollapsed = (snapshot: VisualSnapshot) => {
          const size = snapshot.group ? this.groupMediaState.size : this.mediaSizeFor(conceptId)
          return this.$el.contains(snapshot.element) && size === 'thumbnail'
        }
        const collapsed = snapshots.filter(isCollapsed)
        if (!collapsed.length) {
          this.transitionActive = false
          const expanded = (previousSize === 'thumbnail'
            && this.mediaSizeFor(conceptId) === 'default')
            || (previousGroupSize === 'thumbnail' && this.groupMediaState.size === 'default')
          const keepExpanded = expanded
            && (item.multiple || (focused && document.activeElement === focused))
          if (interruptedMultiple || keepExpanded) {
            preservePosition()
          }
          return
        }
        const motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
        const reducedMotion = motionPreference?.matches === true
        const animations: Animation[] = []
        try {
          if (!reducedMotion) {
            for (const snapshot of collapsed) {
              const { element, rect, marginBottom } = snapshot
              const after = element.getBoundingClientRect()
              if (!element.animate || !rect.height || !after.height) continue
              animations.push(element.animate([
                { width: `${rect.width}px`, height: `${rect.height}px`, marginBottom,
                  minHeight: '0px', maxHeight: 'none', boxSizing: 'border-box' },
                { width: `${after.width}px`, height: `${after.height}px`,
                  marginBottom: window.getComputedStyle(element).marginBottom,
                  minHeight: '0px', maxHeight: 'none', boxSizing: 'border-box' },
              ], { duration: 180, easing: 'ease-in-out' }))
            }
          }
          this.resizeAnimations = animations
          if (item.multiple) {
            const keepPosition = () => {
              if (transitionId !== this.transitionId) return
              preservePosition()
              this.positionFrame = window.requestAnimationFrame(keepPosition)
            }
            keepPosition()
          }
          await Promise.all(animations.map(animation => animation.finished))
        } catch {
          // A newer action may cancel an animation, or a browser may not support it.
        } finally {
          for (const animation of animations) animation.cancel()
          if (transitionId === this.transitionId) {
            this.resizeAnimations = []
            if (this.positionFrame !== null) window.cancelAnimationFrame(this.positionFrame)
            this.positionFrame = null
            if (item.multiple) preservePosition()
            this.transitionActive = false
          }
        }
        if (transitionId !== this.transitionId || !collapsed.some(isCollapsed)) return
        if (item.multiple) return
        const currentIndex = this.visibleQuestionItems.findIndex(
          item => item.conceptId === conceptId,
        )
        if (currentIndex < 0) return
        const targetId = this.visibleQuestionItems[currentIndex + 1]?.conceptId || conceptId
        const target = this.questionElement(targetId)
        target?.scrollIntoView?.({ block: 'start', behavior: reducedMotion ? 'auto' : 'smooth' })
      })
    },
    mediaSizeFor(conceptId: string): string {
      return this.mediaState[conceptId]?.size ?? 'default'
    },
    audioEnabledFor(conceptId: string): boolean {
      return this.mediaState[conceptId]?.audioEnabled ?? false
    },
    withAnchor(index: number, emit: () => void): void {
      const conceptId = this.visibleQuestionItems[index]?.conceptId
      const el = this.questionElement(conceptId)
      const before = el?.getBoundingClientRect().top ?? 0
      this.cancelTransitions()
      const transitionId = this.transitionId
      emit()
      this.$nextTick(() => {
        if (transitionId !== this.transitionId) return
        const after = el?.getBoundingClientRect().top ?? before
        const delta = after - before
        if (delta) window.scrollBy(0, delta)
      })
    },
  },
})
</script>
